// src/encounter/lib/add-line-sections.ts
//
// PROD-3041 · frame v2. The model behind the ONE "Add diagnosis" / "Add order" line of the A&P blocks
// inside the note (live dev review, 6 Oct; Samer lifted the freeze at about 19:05 the same evening).
// PURE: no React, no I/O.
//
// What it decides, and nothing else:
//   · the rows of each section, in the order the clinician reads them: favourites first, then the
//     rest of the catalogue, then order sets (a catalogue row that is also a favourite is dropped
//     from "the rest", so a code is offered once);
//   · which sections show: every non-empty one, with EVERY row (PROD-3082, Andrew, 7 Oct: "don't cut the
//     search results to 8 … a fixed-height list that scrolls, so the panel doesn't grow"). The DS
//     CommandMenu holds them in its fixed-height scroll list (`max-h-80 overflow-y-auto`), so law L2
//     (at most 8 rows in view at once) still holds while nothing is cut;
//   · a row's two lines: the TERM first, then its code and system ("M54.50 · ICD-10"). No score, no
//     percentage, no bar (law L4: a lexical match score is never shown as confidence).
//
// What it deliberately does NOT do (recorded so nobody adds it from words):
//   · a "related to the chief complaint" tier. complaint-map-v2.json maps a complaint to ROS / exam
//     systems and phrases only; it holds no ICD codes, so there is nothing to rank by. Skipped.
//   · a sex or age filter. The ICD-10 catalogue (Scrips.Master `ICD10`: Code, Display, Definition,
//     System, PreferredDisplay; read on origin 2026-10-06) and the ConditionCode wire carry no sex or
//     age restriction. A filter guessed from the words of a term would be an invented clinical rule.
//     When the catalogue carries a restriction, filter here.

/** The most rows a list shows at once (law L2). The Intelligence suggestion list still cuts to it; the A&P add lines
 *  no longer do (PROD-3082): they list every result in the DS menu's fixed-height scroll list. */
export const ADD_LINE_MAX_ROWS = 8;

export interface AddLineRow<T> {
  /** Unique across ALL sections of one dropdown (the section id is part of it). */
  key: string;
  /** The term, read first. */
  term: string;
  /** The second, quieter line: code and system, or the order type and code. */
  meta?: string;
  /** What the pick hands back. */
  value: T;
}

export interface AddLineSection<T> {
  id: string;
  /** The section heading, e.g. "Favourites", "Labs", "Order sets". */
  label: string;
  rows: AddLineRow<T>[];
}

/** The sections a dropdown shows: every non-empty one, with every row, in order (PROD-3082: nothing is cut; the DS
 *  menu scrolls). Empty sections are dropped; the order of sections and of rows inside a section never changes. */
export function liveSections<T>(sections: readonly AddLineSection<T>[]): AddLineSection<T>[] {
  return sections.filter((s) => s.rows.length > 0);
}

/** How many rows the sections hold in all. */
export function rowCount<T>(sections: readonly AddLineSection<T>[]): number {
  return sections.reduce((n, s) => n + s.rows.length, 0);
}

/** "M54.50 · ICD-10" — code then system, either may be absent. Never a score. */
export function codeMeta(code?: string | null, system?: string | null): string | undefined {
  const parts = [code?.trim(), system?.trim()].filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/** The comparable form of a code: trimmed, upper-case (ICD-10 codes are case-insensitive). */
const codeKey = (code?: string | null): string => (code ?? '').trim().toUpperCase();

/**
 * Drop the rows whose code is already offered above (a favourite listed again in the catalogue). A row
 * with no code is kept: there is nothing to compare it by.
 */
export function withoutCodes<T extends { code?: string | null }>(rows: readonly T[], taken: readonly (string | null | undefined)[]): T[] {
  const seen = new Set(taken.map(codeKey).filter(Boolean));
  return rows.filter((r) => {
    const k = codeKey(r.code);
    return !k || !seen.has(k);
  });
}
